/*
tslint:disable:import-spacing no-inferrable-types
variable-name align no-trailing-whitespace component-class-suffix
no-string-literal component-selector
*/

import {
  AfterViewInit,
  OnDestroy,
  Component,
  EventEmitter,
  Input,
  Output
} from '@angular/core';

import {
  BehaviorSubject,
  Subscription
} from 'rxjs';

import {TSO}                       from '@/models/scenario/t.s.o';

import {Scenario}                  from '@/models/scenario/scenario';
import {Logger}                    from '@/util/logger';
import {Period}                    from '@/util/period';
import {UnDoRedo}                  from '@/util/un.do.redo';

import {Track}                     from '@/models/scenario/track';

import {TrainClass}                from '@/models/scenario/train.class';

import {NameRenderer}              from '@/helpers/name.renderer';
import {DescriptionRenderer}       from '@/helpers/description.renderer';
import {WholeNumberRenderer}       from '@/helpers/whole.number.renderer';
import {WholeNumberEditor}         from '@/helpers/whole.number.editor';
import {WholePositiveNumberEditor} from '@/helpers/whole.positive.number.editor';
import {NumberRenderer}            from '@/helpers/number.renderer';
import {NumberEditor}              from '@/helpers/number.editor';
import {PositiveNumberEditor}      from '@/helpers/positive.number.editor';
import {TSOChange}                 from '@/screens/ide/tso/t.s.o.change';
import {PeriodEditor}              from "@/helpers/period.editor";
import {PeriodRenderer}            from "@/helpers/period.renderer";

import {TrainClassesEditor}        from "./train.classes.editor";
import {TrainClassesRenderer}      from "@/helpers/train.classes.renderer";

import {ITSOInputCapture}          from './i.t.s.o.input.capture';

import {ICommand}                  from '@/util/i.command';
import {SetNameCommand}            from './set.name.command';
import {SetDescriptionCommand}     from './set.description.command';
import {SetPSGSpeedCommand}        from './set.p.s.g.speed.command';
import {SetFRTSpeedCommand}        from './set.f.r.t.speed.command';
import {SetPSGClassesCommand}      from './set.p.s.g.classes.command';
import {SetFRTClassesCommand}      from './set.f.r.t.classes.command';
import {SetDurationCommand}        from './set.duration.command';
import {SetStartCommand}           from './set.start.command';
import {SetWestMileCommand}        from './set.west.mile.command';
import {SetEastMileCommand}        from './set.east.mile.command';
import {SetTonnageCommand}         from './set.tonnage.command';

import {TSOInlineEdit}             from './t.s.o.inline.edit';

import 'ag-grid-community';
import 'ag-grid-enterprise';

import {
  CellClassParams,
  CellPosition,
  CellValueChangedEvent,
  Column,
  GetContextMenuItemsParams,
  GridApi,
  GridReadyEvent,
  IRowNode,
  TabToNextCellParams
} from 'ag-grid-community';


@Component({
  selector: 'tso-editor',
  template: `
    <div
      id="tso-container-wrapper_{{uuid}}"
      class="tso-container-wrapper"
      style="display: block; float: left; padding: 8px; width: 100%; height: 100%;">

      <span style="height: 32px; width: 100%; display: block;" [innerHTML]="errorText"></span>
      <ag-grid-angular
        id="ag-grid-tso-editor_{{uuid}}" style="width: 100%; height: calc(100% - 32px);"
        class="ag-theme-balham-dark ag-grid-tso-editor"
        [rowData]="rowData"
        [columnDefs]="columnDefs"
        [defaultColDef]="defaultColDef"
        [context]="context"
        [animateRows]="true"
        [enableRangeSelection]="true"

        [allowContextMenuWithControlKey]="true"
        [getContextMenuItems]="getContextMenuItems"

        [tabToNextCell]="tabToNextCell"

        [components]="components"

        (gridReady)="onGridReady($event)"

      >
      </ag-grid-angular>
    </div>

   `
})

/**
 *
 * @class TSOEditor
 *
 * @brief editor for just the diversions section of a scenario
 * - - - - - - - - - - - - - - - - - - -
 * @author    Michael Garvin (mike.garvin@ironroad.ca)
 * @date      July 2020
 *
 */
export class TSOEditor implements AfterViewInit, OnDestroy, ITSOInputCapture {

  public          errorText: string                               = '<i class="wbedit-info-line">Ready.</i>';

  @Input()             uuid: number                               = 0;

  /** the history manager (not owned by us */
  private          _history: UnDoRedo                             = null;

  /** @var _scenario the scenario model */
  private         _scenario: Scenario                             = null;

  /** the currently active row/selection/Diversion */
  private selectedDiversion: BehaviorSubject<TSOChange>     = null;
  private         selectSub: Subscription                         = null;

  private    lastInlineEdit: BehaviorSubject<TSOInlineEdit> = null;
  private           editSub: Subscription                         = null;

  /** validation related flags */
  private       _inputValid: boolean                              = false;
  private        _canDelete: boolean                              = false;
  private         _hasInput: boolean                              = false;

  /** the API for the widget */
  private               api: GridApi                              = null;

  private      isFullScreen: boolean                              = false;

  public context  = {

    tso: this

  };

  public defaultColDef           = {
    sortable:  true,
    filter:    true,
    resizable: true
  };

  public rowData                 = [{
    id:          '',
    row:         0,
    name:        '',
    eastMile:    '',
    tonnage:     '',
    westMile:    '',
    start:       '',
    duration:    '',
    frtClasses:  [],
    frtSpeed:    '',
    psgClasses:  [],
    psgSpeed:    '',
    description: ''
  }];

  @Output() edit                 = new EventEmitter<Scenario>();

  public components = {
    trainClassEditor:          TrainClassesEditor,
    numberEditor:              NumberEditor,
    positiveNumberEditor:      PositiveNumberEditor,
    wholeNumberEditor:         WholeNumberEditor,
    wholePositiveNumberEditor: WholePositiveNumberEditor,
    periodEditor:              PeriodEditor,
    durationEditor:            PeriodEditor
  };

  public columnDefs: any[]       = [

    {
      headerName: 'ID',
      field: 'id',
      editable: false,
      width: 72
    },

    {
      headerName: 'Name',
      field: 'name',
      cellRenderer: NameRenderer,

      onCellValueChanged: (e: CellValueChangedEvent) => {

        e.context.tso.nameChanged(e.oldValue, e.newValue);
        e.context.tso.validate();
      },
      cellClassRules: {
        'work-input-error': (params: CellClassParams) => {

          return ((typeof params.value === 'undefined') ||
            (params.value === null) ||
            (params.value.trim() === ''));
        }
      },
      editable: true,
      width: 150,
    },

    {
      headerName: 'Tonnage Release',
      field: 'tonnage',
      cellRenderer: WholeNumberRenderer,
      editable: true,
      width: 150,
      cellEditorParams: {
        cellRenderer: WholeNumberRenderer,
      },
      cellEditor:   'wholePositiveNumberEditor',
      cellDataType: 'number',
      onCellValueChanged: (e: CellValueChangedEvent): void => {

        e.context.tso.tonnageChanged(e.oldValue, e.newValue);
        e.context.tso.validate();
      },
      cellClassRules: {
        'work-input-error': (params: CellClassParams) => {

          if (typeof params.value === 'number') {

            return params.value < 0;
          }

          return ((typeof params.value === 'undefined') ||
            (params.value === null) ||
            (params.value.trim() === '') ||
            (params.value < 0));
        }
      }
    },

    {
      headerName: 'Start MP',
      field: 'eastMile',
      cellRenderer: NumberRenderer,
      editable: true,
      width: 150,
      cellEditorParams: {
        cellRenderer: NumberRenderer,
      },
      cellEditor:   'positiveNumberEditor',
      cellDataType: 'number',
      onCellValueChanged: (e: CellValueChangedEvent) => {

        e.context.tso.eastMileChanged(e.oldValue, e.newValue);
        e.context.tso.validate();
      },
      cellClassRules: {
        'work-input-error': (params: CellClassParams) => {

          const row: IRowNode = params.context.tso.firstRow();

          if (row !== null) {

            let e: number = parseFloat(row.data['eastMile']);
            let w: number = parseFloat(row.data['westMile']);

            if (isNaN(e)) {

              e = 0;
            }

            if (isNaN(w)) {

              w = 0;
            }

            if ((e <= 0) && (w <= 0)) {

              /* at least one of them has to be filled in */

              return true;
            }

            if (e > w) {

              /* backwards */

              return true;
            }
          }

          /* usual checks */

          if (typeof params.value === 'number') {

            return params.value < 0;
          }

          return ((typeof params.value === 'undefined') ||
            (params.value === null) ||
            (params.value.trim() === '') ||
            (params.value < 0));
        }
      }
    },

    {
      headerName: 'End MP',
      field: 'westMile',
      cellRenderer: NumberRenderer,
      editable: true,
      width: 150,
      cellEditorParams: {
        cellRenderer: NumberRenderer,
      },
      cellEditor:   'positiveNumberEditor',
      cellDataType: 'number',
      onCellValueChanged: (e: CellValueChangedEvent) => {

        e.context.tso.westMileChanged(e.oldValue, e.newValue);
        e.context.tso.validate();
      },
      cellClassRules: {
        'work-input-error': (params: CellClassParams) => {

          const row: IRowNode = params.context.tso.firstRow();

          if (row !== null) {

            let e: number = parseFloat(row.data['eastMile']);
            let w: number = parseFloat(row.data['westMile']);

            if (isNaN(e)) {

              e = 0;
            }

            if (isNaN(w)) {

              w = 0;
            }

            if ((e <= 0) && (w <= 0)) {

              /* at least one of them has to be filled in */

              return true;
            }

            if (e > w) {

              /* backwards */

              return true;
            }
          }

          /* usual checks */

          if (typeof params.value === 'number') {

            return params.value < 0;
          }

          return ((typeof params.value === 'undefined') ||
            (params.value === null) ||
            (params.value.trim() === '') ||
            (params.value < 0));
        }
      }
    },

    {
      headerName:   'Begin',
      field:        'start',
      cellRenderer:  PeriodRenderer,
      cellEditor:   'periodEditor',
      cellDataType: 'object',
      equals:       (a: Period, b: Period): boolean =>  {

        if (!( a instanceof Period ) || !( b instanceof Period )) {

          return a === b;
        }

        return a.asString() === b.asString();
      },

      onCellValueChanged: (e: CellValueChangedEvent) => {

        e.context.tso.startChanged(e.oldValue, e.newValue);
        e.context.tso.validate();
      },

      cellClassRules: {
        'work-input-error': (params: CellClassParams) => {

          if (params.value === '') {

            return true;
          }

          return ((typeof params.value === 'undefined') || (params.value === null));
        }
      },

      editable:     true,
      width:        150
    },

    {
      headerName:  'Duration',
      field:       'duration',
      cellRenderer: PeriodRenderer,
      cellEditor:   'durationEditor',
      cellDataType: 'object',
      equals:       (a: Period, b: Period): boolean =>  {

        if (!( a instanceof Period ) || !( b instanceof Period )) {

          return a === b;
        }

        return a.asString() === b.asString();
      },

      onCellValueChanged: (e: CellValueChangedEvent) => {

        e.context.tso.durationChanged(e.oldValue, e.newValue);
        e.context.tso.validate();
      },

      cellClassRules: {
        'work-input-error': (params: CellClassParams) => {

          if (params.value === '') {

            return true;
          }

          return ((typeof params.value === 'undefined') || (params.value === null));
        }
      },

      editable:     true,
      width:        150
    },

    {
      headerName: 'FRT Classes',
      field: 'frtClasses',
      cellRenderer: TrainClassesRenderer,
      editable: true,
      width: 150,
      cellEditorParams: {
        cellRenderer: TrainClassesRenderer,
      },
      cellEditor:   'trainClassEditor',
      equals:       (a: TrainClass[], b: TrainClass[]): boolean =>  {

        if (!Array.isArray(a) || !Array.isArray(b)) {

          return a === b;
        }

        const aa:number[] = a.map(x => x._id);
        const bb:number[] = b.map(x => x._id);

        aa.sort();
        bb.sort();

        return JSON.stringify(aa) === JSON.stringify(bb);
      },

      onCellValueChanged: (e: CellValueChangedEvent) => {

        e.context.tso.frtClassesChanged(e.oldValue, e.newValue);
        e.context.tso.validate();
      },
      cellClassRules: {
        'work-input-error': (params: CellClassParams) => {

          if (!this.bucketsOk()) {

            return true;
          }

          return ((typeof params.value === 'undefined') || (params.value === null));
        }
      }
    },

    {
      headerName: 'FRT Speed',
      field: 'frtSpeed',
      cellRenderer: NumberRenderer,
      editable: true,
      width: 150,
      cellEditorParams: {
        cellRenderer: NumberRenderer,
      },
      cellEditor:   'positiveNumberEditor',
      cellDataType: 'number',
      onCellValueChanged: (e: CellValueChangedEvent) => {

        e.context.tso.frtSpeedChanged(e.oldValue, e.newValue);
        e.context.tso.validate();
      },
      cellClassRules: {
        'work-input-error': (params: CellClassParams) => {

          /* make sure at least one speed is filled in */

          const     row: IRowNode = params.context.tso.firstRow();

          if (row !== null) {

            let csfrt: number = parseFloat(row.data['frtSpeed']);
            let cspsg: number = parseFloat(row.data['psgSpeed']);

            if (isNaN(csfrt)) {
              csfrt = 0;
            }

            if (isNaN(cspsg)) {
              cspsg = 0;
            }

            if ((csfrt <= 0) && (cspsg <= 0)) {

              /* at least one of them has to be filled in */

              return true;
            }
          }

          if (!this.bucketsOk()) {

            return true;
          }

          /* usual checks */

          if (typeof params.value === 'number') {

            return params.value < 0;
          }

          return false;
        }
      }
    },

    {
      headerName: 'PSG Classes',
      field: 'psgClasses',
      cellRenderer: TrainClassesRenderer,
      editable: true,
      width: 150,
      cellEditorParams: {
        cellRenderer: TrainClassesRenderer,
      },
      cellEditor:   'trainClassEditor',
      equals:       (a: TrainClass[], b: TrainClass[]): boolean =>  {

        if (!Array.isArray(a) || !Array.isArray(b)) {

          return a === b;
        }

        const aa:number[] = a.map(x => x._id);
        const bb:number[] = b.map(x => x._id);

        aa.sort();
        bb.sort();

        return JSON.stringify(aa) === JSON.stringify(bb);
      },

      onCellValueChanged: (e: CellValueChangedEvent) => {

        e.context.tso.psgClassesChanged(e.oldValue, e.newValue);
        e.context.tso.validate();
      },
      cellClassRules: {
        'work-input-error': (params: CellClassParams) => {

          if (!this.bucketsOk()) {

            return true;
          }

          return ((typeof params.value === 'undefined') || (params.value === null));
        }
      }
    },

    {
      headerName: 'PSG Speed',
      field: 'psgSpeed',
      cellRenderer: NumberRenderer,
      editable: true,
      width: 150,
      cellEditorParams: {
        cellRenderer: NumberRenderer,
      },
      cellEditor:   'positiveNumberEditor',
      cellDataType: 'number',
      onCellValueChanged: (e: CellValueChangedEvent): void => {

        e.context.tso.psgSpeedChanged(e.oldValue, e.newValue);
        e.context.tso.validate();
      },
      cellClassRules: {
        'work-input-error': (params: CellClassParams): boolean => {

          /* make sure at least one speed is filled in */

          const     row: IRowNode = params.context.tso.firstRow();

          if (row !== null) {

            let csfrt: number = parseFloat(row.data['frtSpeed']);
            let cspsg: number = parseFloat(row.data['psgSpeed']);

            if (isNaN(csfrt)) {
              csfrt = 0;
            }

            if (isNaN(cspsg)) {
              cspsg = 0;
            }

            if ((csfrt <= 0) && (cspsg <= 0)) {

              /* at least one of them has to be filled in */

              return true;
            }
          }

          if (!this.bucketsOk()) {

            return true;
          }

          /* usual checks */

          if (typeof params.value === 'number') {

            return params.value < 0;
          }

          return false;
        }
      }
    },

    {
      headerName:   'Description',
      cellRenderer: DescriptionRenderer,
      field:        'description',
      cellDataType: 'text',
      onCellValueChanged: (e: CellValueChangedEvent) => {

        e.context.tso.descriptionChanged(e.oldValue, e.newValue);
        e.context.tso.validate();
      },

      editable:     true,
      width:        150
    }

  ];

  constructor(

  ) {

  }

  /** update the error line in the loader drop down */
  public status(msg: string =  '') {

    this.errorText = `<i class="wbedit-info-line">${msg}</i>`;
    Logger.info(`[tsoeditor] ${msg}`);
  }

  /** update the error line in the loader drop down */
  public success(msg: string =  '') {

    this.errorText = `<i class="wbedit-success-line">${msg}</i>`;
    Logger.debug(`[tsoeditor] ${msg}`);
  }

  /** update the error line in the loader drop down */
  public error(msg: string =  '') {

    this.errorText = `<i class="wbedit-error-line">${msg}</i>`;
    Logger.error(`[tsoeditor] ${msg}`);
  }

  private frtBucketValid(): boolean {

    const row: IRowNode      = this.firstRow();
    let    fc: TrainClass[] = [];

    if (row === null) {

      return false;
    }

    let     f: number       = parseInt(row.data.frtSpeed);

    if (Array.isArray(row.data.frtClasses)) {

      fc = row.data.frtClasses;
    }

    /* if they fill in one or the other...both have to be filled in */

    if ((f <= 0) && (fc.length > 0) ||
        (f > 0)  && (fc.length === 0)) {

      this.error('FRT class and speed must both be set (speed > 0.0).');

      return false;
    }

    return true;
  }

  private frtBucketFilled(): boolean {

    const row: IRowNode      = this.firstRow();

    if (row === null) {

      return false;
    }

    let f: number = parseInt(row.data.frtSpeed);

    if (isNaN(f)) {

      f = 0;
    }

    return f > 0;
  }

  private psgBucketValid(): boolean {

    const row: IRowNode      = this.firstRow();
    let    pc: TrainClass[] = [];

    if (row === null) {

      return false;
    }

    let     p: number       = parseInt(row.data.psgSpeed);

    if (Array.isArray(row.data.psgClasses)) {

      pc = row.data.psgClasses;
    }

    /* if they fill in one or the other...both have to be filled in */

    if ((p <= 0) && (pc.length > 0) ||
        (p > 0)  && (pc.length === 0)) {

      this.error('PSG class and speed must both be set (speed > 0.0).');

      return false;
    }

    return true;
  }

  private psgBucketFilled(): boolean {

    const row: IRowNode      = this.firstRow();

    if (row === null) {

      return false;
    }

    let p: number = parseInt(row.data.psgSpeed);

    if (isNaN(p)) {

      p = 0;
    }

    return p > 0;
  }

  private bucketsOk(): boolean {

    /* we have to one or both of FRT or PSG speed buckets filled in.  It can't be none. */

    let a1: boolean = this.frtBucketFilled();
    let a2: boolean = this.psgBucketFilled();

    if (!a1 && !a2) {

      this.error("Must set at least one of FRT or PSG.");

      return false;
    }

    /* they picked train classes for a bucket, they have to have the speed for the bucket, and vice versa */

    let b1: boolean = this.frtBucketValid();
    let b2: boolean = this.psgBucketValid();

    if (!b1 || !b2) {

      return false;
    }

    return true;
  }

  /** scan all the editor inputs, and set up our validity flags */
  private validate(): void {

    const  fields: string[] = [
      'name',
      'tonnage',
      'eastMile',
      'westMile',
      'start',
      'duration',
      'frtClasses',
      'frtSpeed',
      'psgClasses',
      'psgSpeed'
    ];

    const     row: IRowNode  = this.firstRow();
    let   invalid: boolean  = false;

    this.status('Validating...');

    /* if it has non-zero tso id...then its one we can either clone or delete */

    this._canDelete = this.currentId() > 0;

    /* if any of the inputs are non-empty (not '', null or undefined), then we have "some" input */

    this._hasInput = false;

    fields.forEach((item: string) => {

      const datum: any = row.data[item];

      if (typeof datum === 'undefined') {

        this.error(`${item} can not be empty.`);
        invalid = true;
      }

      if (datum === null) {

        this.error(`${item} can not be empty.`);
        invalid = true;
      }

      if ((item === 'name') && ((datum === null) || (datum === '') || (typeof datum === 'undefined'))) {

        this.error("name can not be empty.");

        invalid = true;
        return ;
      }

      if ((item === 'tonnage') && (isNaN(datum) || (datum === ''))) {

        this.error("tonnage is not a valid number.");

        invalid = true;
        return ;
      }

      if ((item === 'eastMile') && (isNaN(datum) || (datum === ''))) {

        this.error("start (eastern) mileage is not a valid number.");

        invalid = true;
        return ;
      }

      if ((item === 'westMile') && (isNaN(datum) || (datum === ''))) {

        this.error("end (western) mileage is not a valid number.");

        invalid = true;
        return ;
      }

      if ((item === 'start') && !(datum instanceof Period)) {

        this.error("start time must be  set.");

        invalid = true;
        return ;
      }

      if ((item === 'duration') && !(datum instanceof Period)) {

        this.error("duration must be set.");

        invalid = true;
        return ;
      }

      /* at least one column has non-empty input */

      this._hasInput = true;
    });

    /* east mileage must be lower than west mileage */

    {
      let em: number = parseFloat(row.data.eastMile);
      let wm: number = parseFloat(row.data.westMile);

      if (isNaN(em)) {

        em = 0;
      }

      if (isNaN(wm)) {

        wm = 0;
      }

      if (wm < em) {

        /* mileage bracket is backwards */

        this.error("mileage markers are backwards.");

        invalid = true;

      }
    }

    /* make sure one or both buckets are usable */

    if (!this.bucketsOk()) {

      invalid = true;
    }

    if (!invalid) {

      this.success('Validated: OK.');
    }

    /* the input is only valid if all inputs are filled in */

    this._inputValid = !invalid;
  }

  /** check to see if the input is valid (can add as WorkBlock) */
  public isValid(): boolean {

    return this._inputValid;
  }

  /** check to see if the input is an edit of an existing workblock (i.e. ID > 0) */
  public  isDeletable(): boolean {

    return this._canDelete;
  }

  /** check to see if the there is any input, either user entered or a workblock being edited. */
  public hasInput(): boolean {

    return this._hasInput || this._canDelete;
  }

  tabToNextCell(params: TabToNextCellParams): CellPosition  {

    /* if we're going outside the table...stay on target, stay on target! */

    if ( (typeof params.nextCellPosition === 'undefined') || (params.nextCellPosition === null)) {

      return params.previousCellPosition;
    }

    return params.nextCellPosition;
  }

  onGridReady(params: GridReadyEvent): void {

    this.api       = params.api;

    this.focusCell('name');
  }

  /** capture the current user inputs */
  captureInput(): TSOChange {

    /* there is only one row, get the data from the row, and convert to a snapshot */

    const snapshot: TSOChange = new TSOChange();
    const      row: IRowNode   = this.firstRow();

    let em: number = parseFloat(row.data.eastMile);
    let wm: number = parseFloat(row.data.westMile);

    if (isNaN(em)) {

      em = 0;
    }

    if (isNaN(wm)) {

      wm = 0;
    }

    if (wm < em) {

      /* mileage increases to the west, so we have to swap */

      let tmp: number = wm;

      wm = em;
      em = tmp;
    }

    let t: number = parseInt(row.data.tonnage);

    if (isNaN(t)) {

      t = 0;
    }

    let f: number = parseInt(row.data.frtSpeed);

    if (isNaN(f)) {

      f = 0;
    }

    let p: number = parseInt(row.data.psgSpeed);

    if (isNaN(p)) {

      p = 0;
    }

    snapshot.id          = row.data.id          ? row.data.id          : '';
    snapshot.name        = row.data.name        ? row.data.name        : '';
    snapshot.tonnage     = t;
    snapshot.eastMile    = em;
    snapshot.eastDP      = this._scenario.lowestDecisionPoint(em);
    snapshot.westMile    = wm;
    snapshot.westDP      = this._scenario.lowestDecisionPoint(wm);
    snapshot.frtSpeed    = f;
    snapshot.psgSpeed    = p;
    snapshot.frtClasses  = row.data.frtClasses  ? row.data.frtClasses  : [];
    snapshot.psgClasses  = row.data.psgClasses  ? row.data.psgClasses  : [];
    snapshot.description = row.data.description ? row.data.description : '';

    snapshot.start       = (row.data.start instanceof Period)    ? row.data.start    : new Period('P00DT00H00M00S');
    snapshot.duration    = (row.data.duration instanceof Period) ? row.data.duration : new Period('P00DT00H00M00S');

    /* pass it back! */

    return snapshot;
  }

  /** helper to focus on the given column / field */
  focusCell(colName: string): void {

    let     cols: Column[]  = this.api.getAllDisplayedColumns();
    let col: Column         = cols.find((x: Column): boolean => {return x.getId() === colName});

    /* find the column ... */

    if ((col === null) || (typeof col === 'undefined')) {

      return ;
    }

    /* focus on that cel */

    this.api.ensureColumnVisible(col);
    this.api.setFocusedCell(0, col);
  }

  /** restore the user input to what they are in this snapshot */
  restoreInput(snapshot: TSOChange): void {

    this.status(`Loading input...`);

    Logger.debug('[tsoeditor] restoring input: ', snapshot);

    /* there is only one row, set the values from this snapshot, without change detection */

    /* create the row data (there is only one row!) */

    /* non-change detection update */

    const row: IRowNode = this.firstRow();

    row.data.id          = snapshot.id;
    row.data.name        = snapshot.name;
    row.data.start       = snapshot.start;
    row.data.duration    = snapshot.duration;
    row.data.tonnage     = snapshot.tonnage;
    row.data.eastMile    = snapshot.eastMile;
    row.data.westMile    = snapshot.westMile;
    row.data.frtSpeed    = snapshot.frtSpeed;
    row.data.psgSpeed    = snapshot.psgSpeed;
    row.data.frtClasses  = snapshot.frtClasses;
    row.data.psgClasses  = snapshot.psgClasses;
    row.data.description = snapshot.description;

    this.status(`Loaded.`);

    /* force a redraw, but without change detection (which could be an infinite loop) */

    this.api.redrawRows({rowNodes: [row]});
    this.validate();

  }

  /** handle single column edits in the edit input area */
  setInlineEditSubject(edit: BehaviorSubject<TSOInlineEdit>) {

    if (this.editSub !== null) {

      /* unsubscribe old one */

      this.editSub.unsubscribe();
    }

    /* subscribe to this selection watcher ... */

    this.lastInlineEdit = edit;
    this.editSub        = null;

  }

  /** set a new observer to use for watching for selection changes */
  public setSelectionSubject(selection: BehaviorSubject<TSOChange>) {

    if (this.selectSub !== null) {

      /* unsubscribe old one */

      this.selectSub.unsubscribe();
    }

    /* subscribe to this selection watcher ... */

    this.selectedDiversion = selection;
    this.selectSub         = selection.subscribe( (next: TSOChange) => {

      this.setSelected(next);
    });

  }

  /** handle somebody change the selection somewhere */
  public setSelected(change: TSOChange) {

    if (change === null) {

      /* just clear any open edits */

      this.clear();

      return ;
    }

    if (change.snapshot !== null) {

      /* this selection has a capture of previous user inputs, we use those instead */

      change = change.snapshot;
    }

    /* check that we aren't calling our self */

    if ( (change.src instanceof TSOEditor) && (change.src === this)) {

      /* from self, ignore infinite looping! */

      return ;
    }

    /* handle the selection update */
    Logger.debug('[tsoeditor] selection change....');

    this.restoreInput(change);
  }

  public getContextMenuItems(params: GetContextMenuItemsParams): any {

    return [];
  }

  /** @brief first time setup of view */
  ngAfterViewInit() {

    Logger.debug('[tsoeditor] view init...');

    this.clear();

  }

  ngOnDestroy(): void {

    if (this.selectSub !== null) {

      /* unsubscribe our observer */

      this.selectSub.unsubscribe();
    }

    if (this.editSub !== null) {

      /* unsubscribe our observer */

      this.editSub.unsubscribe();
    }
  }

  /** clear the user input area */
  public clear() {

    Logger.debug('[tsoeditor] clearing...');

    if (!this.api) {

      /* api isn't even available yet */

      return ;
    }

    /* clearing just means all input columns are empty */

    this.api.setGridOption('rowData', [{
      id:          '',
      row:         0,
      name:        '',
      start:       '',
      duration:    '',
      tonnage:     '',
      eastMile:    '',
      westMile:    '',
      frtSpeed:    '',
      psgSpeed:    '',
      frtClasses:  [],
      psgClasses:  [],
      description: ''
    }]);

  }

  /** fetch current row ID (if there is one */
  public currentId(): number {

    const row: IRowNode = this.firstRow();

    const result: number = row.data?.id;

    if (result === null) {

      return 0;
    }

    if (typeof result === 'undefined') {

      return 0;
    }

    if ( isNaN(result) ) {

      return 0;
    }

    /* looks legit, pass it back */

    return result;
  }

  /** fetch the first row */
  public firstRow(): IRowNode {

    const items: IRowNode[] = [];

    if (this.api === null) {

      /* not ready yet */

      return null;
    }

    try {

      this.api.forEachNode((rowNode: IRowNode): void => {

        items.push(rowNode);
      });

    } catch (e) {

      /* not ready yet */

      return null;
    }

    return items[0];
  }

  /** helper to fetch the actual TSO for the given row */
  public tsoOfRow(id: number): TSO {

    if (this._scenario === null) {

      return null;
    }

    return Array.from(this._scenario.tso().values()).find(x => x._id === id);
  }

  /** helper to update the actual WorkBlock model in the scenario */
  public updateModel(id: number, field: string, value: any) {

    const tso: TSO = this.tsoOfRow(id);

    if ((tso === null) || (!tso)) {

      /* can't find it */

      return ;
    }

    Logger.debug('[tsoeditor] updating model:', id, field, value);

    switch (field) {

      case 'name': {

          tso._name = value as string;
        }
        break;

      case 'start': {

          tso._start = 0;

          if (value instanceof Period) {

            tso._start = (value as Period).asSeconds();
          }

        }
        break;

      case 'duration': {

          tso._duration = 0;

          if (value instanceof Period) {

            tso._duration = (value as Period).asSeconds();
          }
        }
        break;

      case 'tonnage': {

          if (value === '') {

            value = 0;
          }

          tso._tonnage = value as number;
        }
        break;

      case 'eastMile': {

          if (value === '') {

            value = 0;
          }

          tso._eastMile = value as number;
        }
        break;

      case 'westMile': {

          if (value === '') {

            value = 0;
          }

          tso._westMile = value as number;
        }
        break;

      case 'frtSpeed': {

          if (value === '') {

            value = 0;
          }

          tso._frtSpeed = value as number;
        }
        break;

      case 'psgSpeed': {

          if (value === '') {

            value = 0;
          }

          tso._psgSpeed = value as number;
        }
        break;

      case 'frtClasses': {

          if ((value === '') || (value === null)) {

            value = [];
          }

          tso.setClasses('FRT', value as TrainClass[]);

        }
        break;

      case 'psgClasses': {

          if ((value === '') || (value === null)) {

            value = [];
          }

          tso.setClasses('PSG', value as TrainClass[]);

        }
        break;

      case 'description': {

          /* use the given description */

          tso._description = value as string;
        }
        break;

    }

    /* make sure the low and high mileage are ordered correctly */

    if (tso._eastMile > tso._westMile) {

      const tmp     = tso._eastMile;
      tso._eastMile = tso._westMile;
      tso._westMile = tmp;
    }

    /* if we changed the high/low mileage, then we have to recalculate the links */

    if (['eastMile', 'westMile'].includes(field)) {

      const links: Track[] = this._scenario.westBoundBetween(tso._eastMile, tso._westMile);

      tso.setLinks(links);
    }

    /* publish the edit we just did...so that other views know to update themselves */

    const edit: TSOInlineEdit =  new TSOInlineEdit({
      id,
      field,
      value
    });

    this.lastInlineEdit.next(edit);

  }

  /** react to a change in high mileage */
  public psgSpeedChanged(a: any, b: any) {

    /* update the model */

    const id: number = this.currentId();

    if (id > 0) {

      this.updateModel(id, 'psgSpeed', b);
    }

    /* track the change */

    const cmd: ICommand = new SetPSGSpeedCommand(a, b, this);

    this._history.insert(cmd);
  }

  /** force the high mileage to be this value */
  public setPSGSpeed(value: any) {

    let datum: any = '';

    if ( typeof value === 'number') {

      if (value < 0) {

        value = 0;
      }

      datum = value as number;
    }

    /* get the live row */

    const row: IRowNode = this.firstRow();

    /* set this column */

    row.data.psgSpeed = datum;

    /* force a redraw, but without change detection (which could be an infinite loop) */

    this.api.redrawRows({rowNodes: [row]});
    this.validate();

    this.focusCell('psgSpeed');

    /* update the model */

    const id: number = this.currentId();

    if (id > 0) {

      this.updateModel(id, 'psgSpeed', datum);
    }
  }

  /** react to a change in high mileage */
  public frtSpeedChanged(a: any, b: any) {

    /* update the model */

    const id: number = this.currentId();

    if (id > 0) {

      this.updateModel(id, 'frtSpeed', b);
    }

    /* track the change */

    const cmd: ICommand = new SetFRTSpeedCommand(a, b, this);

    this._history.insert(cmd);
  }

  /** force the high mileage to be this value */
  public setFRTSpeed(value: any) {

    let datum: any = '';

    if ( typeof value === 'number') {

      if (value < 0) {

        value = 0;
      }

      datum = value as number;
    }

    /* get the live row */

    const row: IRowNode = this.firstRow();

    /* set this column */

    row.data.frtSpeed = datum;

    /* force a redraw, but without change detection (which could be an infinite loop) */

    this.api.redrawRows({rowNodes: [row]});
    this.validate();

    this.focusCell('frtSpeed');

    /* update the model */

    const id: number = this.currentId();

    if (id > 0) {

      this.updateModel(id, 'frtSpeed', datum);
    }
  }

  /** react to a change in duration */
  public psgClassesChanged(a: any, b: any) {

    /* update the model */

    const id: number = this.currentId();

    if (id > 0) {

      this.updateModel(id, 'psgClasses', b);
    }

    /* track the change */

    const cmd: ICommand = new SetPSGClassesCommand(a, b, this);

    this._history.insert(cmd);
  }

  /** force the duration to be this value */
  public setPSGClasses(value: any) {

    let datum: any = '';

    if ( Array.isArray(value) ) {

      datum = value as TrainClass[];
    }

    /* get the live row */

    const row: IRowNode = this.firstRow();

    /* set this column */

    row.data.psgClasses = datum;

    /* force a redraw, but without change detection (which could be an infinite loop) */

    this.api.redrawRows({rowNodes: [row]});
    this.validate();

    this.focusCell('psgClasses');

    /* update the model */

    const id: number = this.currentId();

    if (id > 0) {

      this.updateModel(id, 'psgClasses', datum);
    }
  }

  /** react to a change in freight classes */
  public frtClassesChanged(a: any, b: any) {

    /* update the model */

    const id: number = this.currentId();

    if (id > 0) {

      this.updateModel(id, 'frtClasses', b);
    }

    /* track the change */

    const cmd: ICommand = new SetFRTClassesCommand(a, b, this);

    this._history.insert(cmd);
  }

  /** force the freight classes */
  public setFRTClasses(value: any) {

    let datum: any = '';

    if ( Array.isArray(value) ) {

      datum = value as TrainClass[];
    }

    /* get the live row */

    const row: IRowNode = this.firstRow();

    /* set this column */

    row.data.frtClasses = datum;

    /* force a redraw, but without change detection (which could be an infinite loop) */

    this.api.redrawRows({rowNodes: [row]});
    this.validate();

    this.focusCell('frtClasses');

    /* update the model */

    const id: number = this.currentId();

    if (id > 0) {

      this.updateModel(id, 'frtClasses', datum);
    }
  }

  /** react to a change in duration */
  public durationChanged(a: any, b: any): void {

    /* update the model */

    const id: number = this.currentId();

    if (id > 0) {

      this.updateModel(id, 'duration', b);
    }

    /* track the change */

    const cmd: ICommand = new SetDurationCommand(a, b, this);

    this._history.insert(cmd);
  }

  /** force the duration to be this value */
  public setDuration(value: any) {

    let datum: any = '';

    if ( value instanceof Period ) {

      datum = value as Period;
    }

    /* get the live row */

    const row: IRowNode = this.firstRow();

    /* set this column */

    row.data.duration = datum;

    /* force a redraw, but without change detection (which could be an infinite loop) */

    this.api.redrawRows({rowNodes: [row]});
    this.validate();

    this.focusCell('duration');

    /* update the model */

    const id: number = this.currentId();

    if (id > 0) {

      this.updateModel(id, 'duration', datum);
    }
  }

  /** react to a change in start time */
  public startChanged(a: any, b: any) {

    /* update the model */

    const id: number = this.currentId();

    if (id > 0) {

      this.updateModel(id, 'start', b);
    }

    /* track the change */

    const cmd: ICommand = new SetStartCommand(a, b, this);

    this._history.insert(cmd);
  }

  /** force the start to be this value */
  public setStart(value: any) {

    let datum: any = '';

    if ( value instanceof Period ) {

      datum = value as Period;
    }

    /* get the live row */

    const row: IRowNode = this.firstRow();

    /* set this column */

    row.data.start = datum;

    /* force a redraw, but without change detection (which could be an infinite loop) */

    this.api.redrawRows({rowNodes: [row]});
    this.validate();

    this.focusCell('start');

    /* update the model */

    const id: number = this.currentId();

    if (id > 0) {

      this.updateModel(id, 'start', datum);
    }
  }

  /** react to a change in high mileage */
  public westMileChanged(a: any, b: any) {

    /* update the model */

    const id: number = this.currentId();

    if (id > 0) {

      this.updateModel(id, 'westMile', b);
    }

    /* track the change */

    const cmd: ICommand = new SetWestMileCommand(a, b, this);

    this._history.insert(cmd);
  }

  /** force the high mileage to be this value */
  public setWestMile(value: any) {

    let datum: any = '';

    if ( typeof value === 'number') {

      if (value < 0) {

        value = 0;
      }

      datum = value as number;
    }

    /* get the live row */

    const row: IRowNode = this.firstRow();

    /* set this column */

    row.data.westMile = datum;

    /* force a redraw, but without change detection (which could be an infinite loop) */

    this.api.redrawRows({rowNodes: [row]});
    this.validate();

    this.focusCell('westMile');

    /* update the model */

    const id: number = this.currentId();

    if (id > 0) {

      this.updateModel(id, 'westMile', datum);
    }
  }

  /** react to a change in low mileage */
  public eastMileChanged(a: any, b: any) {

    /* update the model */

    const id: number = this.currentId();

    if (id > 0) {

      this.updateModel(id, 'eastMile', b);
    }

    /* track the change */

    const cmd: ICommand = new SetEastMileCommand(a, b, this);

    this._history.insert(cmd);
  }

  /** force low mileage to be this value */
  public setEastMile(value: any) {

    let datum: any = 0;

    if ( typeof value === 'number') {

      if (value < 0) {

        value = 0;
      }

      datum = value as number;
    }

    /* get the live row */

    const row: IRowNode = this.firstRow();

    /* set this column */

    row.data.eastMile = datum;

    /* force a redraw, but without change detection (which could be an infinite loop) */

    this.api.redrawRows({rowNodes: [row]});
    this.validate();

    this.focusCell('eastMile');

    /* update the model */

    const id: number = this.currentId();

    if (id > 0) {

      this.updateModel(id, 'eastMile', datum);
    }
  }

  /** react to a change in tonnage */
  public tonnageChanged(a: number, b: number) {

    /* update the model */

    const id: number = this.currentId();

    if (id > 0) {

      this.updateModel(id, 'tonnage', b);
    }

    /* track the change */

    const cmd: ICommand = new SetTonnageCommand(a, b, this);

    this._history.insert(cmd);
  }

  /** force the tonnage to be this value */
  public setTonnage(value: any) {

    let datum: any = '';

    if ( typeof value === 'number') {

      if (value < 0) {

        value = 0;
      }

      datum = value as number;
    }

    /* get the live row */

    const row: IRowNode = this.firstRow();

    /* set this column */

    row.data.tonnage = datum;

    /* force a redraw, but without change detection (which could be an infinite loop) */

    this.api.redrawRows({rowNodes: [row]});
    this.validate();

    this.focusCell('tonnage');

    /* update the model */

    const id: number = this.currentId();

    if (id > 0) {

      this.updateModel(id, 'tonnage', datum);
    }
  }

  /** react to a tso name change */
  public nameChanged(a: string, b: string): void {

    /* update the model */

    const id: number = this.currentId();

    if (id > 0) {

      this.updateModel(id, 'name', b);
    }

    /* track the change */

    const cmd: ICommand = new SetNameCommand(a, b, this);

    this._history.insert(cmd);
  }

  /** force the name column to be this value */
  public setName(value: string) {

    if (typeof value === 'undefined') {

      value = '';
    }

    /* get the live row */

    const row: IRowNode = this.firstRow();

    /* set this column */

    row.data.name = value;

    /* force a redraw, but without change detection (which could be an infinite loop) */

    this.api.redrawRows({rowNodes: [row]});
    this.validate();

    this.focusCell('name');

    /* update the model */

    const id: number = this.currentId();

    if (id > 0) {

      this.updateModel(id, 'name', value);
    }
  }

  /** react to a tso description change */
  public descriptionChanged(a: string, b: string): void {

    /* update the model */

    const id: number = this.currentId();

    if (id > 0) {

      this.updateModel(id, 'description', b);
    }

    /* track the change */

    const cmd: ICommand = new SetDescriptionCommand(a, b, this);

    this._history.insert(cmd);
  }


  /** force the description column to be this value */
  public setDescription(value: string) {

    if (typeof value === 'undefined') {

      value = '';
    }

    /* get the live row */

    const row: IRowNode = this.firstRow();

    /* set this column */

    row.data.description = value;

    /* force a redraw, but without change detection (which could be an infinite loop) */

    this.api.redrawRows({rowNodes: [row]});
    this.validate();

    this.focusCell('description');

    /* update the model */

    const id: number = this.currentId();

    if (id > 0) {

      this.updateModel(id, 'description', value);
    }
  }

  /** take a reference to the shared history manager (provides undo/redo support) */
  public setHistory(history: UnDoRedo): void {

    this._history = history;
  }

  /** reset to this scenario */
  public setScenario(s: Scenario) {

    /* set the "model" */

    this._scenario = s;

    /* clear the input area */

    this.clear();

    this.status(`New scenario set.`);
  }

  /** fetch the current scenario (if there is one) */
  public getScenario(): Scenario {

    return this._scenario;
  }


}
